import React, { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import apiServices from "../../../../services/apiService";
import MetricCard from "../../../common/Card/MetricCard";
import Chart from "react-apexcharts";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faUsers, faBuilding, faLayerGroup, faDoorClosed, faClock, faWrench, faBullhorn, faUserPlus } from "@fortawesome/free-solid-svg-icons";
import storageService from "../../../../utils/storageService";
import { primaryColor, secondaryColor } from "../../../../utils/comon";
import ActionCenter from "../../../common/ActionCenter/ActionCenter";

const SuperAdminDashboard = () => {
    const navigate = useNavigate();
    const { user: authUser } = useSelector((state) => state.auth);
    const roleId = Number(authUser?.roleId || authUser?.RoleId || storageService.getItem("roleId") || 2);

    const [theme, setTheme] = useState(localStorage.getItem("theme") || "light");
    useEffect(() => {
        const handleThemeChange = () => {
            setTheme(localStorage.getItem("theme") || "light");
        };
        window.addEventListener("themeChanged", handleThemeChange);
        return () => window.removeEventListener("themeChanged", handleThemeChange);
    }, []);
    const isDark = theme === "dark";

    const [loading, setLoading] = useState(true);
    const [dashboardData, setDashboardData] = useState({
        masters: {
            totalSites: 0,
            totalWings: 0,
            totalFloors: 0,
            totalUnits: 0,
            ownedUnits: 0,
            rentedUnits: 0
        },
        users: {
            totalUsers: 0,
            roleWiseCounts: [],
            totalOwnerRecords: 0,
            totalActiveTenantRecords: 0
        },
        visitors: {
            totalVisitors: 0,
            invitedVisitors: 0,
            walkInVisitors: 0,
            approvedVisitors: 0,
            pendingVisitors: 0,
            rejectedVisitors: 0,
            checkOutVisitors: 0,
            cancelledVisitors: 0,
            blockedVisitors: 0,
            checkedInVisitors: 0,
            currentlyOnPremises: 0
        },
        notices: {
            totalNotices: 0,
            publishedNotices: 0,
            draftNotices: 0,
            expiredNotices: 0
        },
        complaints: {
            totalComplaints: 0
        }
    });

    useEffect(() => {
        const fetchDashboardData = async () => {
            try {
                setLoading(true);
                const response = await apiServices({
                    method: "GET",
                    url: `/CommDashboard/Dashboard/GetDashboardSummary?roleId=${roleId}`,
                });

                if (response?.success) {
                    const payload = response.data?.data;
                    setDashboardData(payload);
                }
            } catch (error) {
                console.error("Error fetching super admin dashboard data:", error);
            } finally {
                setLoading(false);
            }
        };

        fetchDashboardData();
    }, [roleId]);

    const { masters, users, visitors, notices, complaints } = dashboardData;

    // ----------------------------------------------------
    // METRICS CONFIGURATION
    // ----------------------------------------------------
    const topMetrics = [
        {
            title: "Total Wings",
            value: masters?.totalWings || 0,
            icon: faLayerGroup,
            color: "text-yellow-500"
        },
        {
            title: "Total Floors",
            value: masters?.totalFloors || 0,
            icon: faBuilding,
            color: "text-red-500"
        },
        {
            title: "Total Units",
            value: masters?.totalUnits || 0,
            icon: faDoorClosed,
            color: "text-blue-500"
        },
        {
            title: "Total Users",
            value: users?.totalUsers || 0,
            icon: faUsers,
            color: "text-green-500"
        }
    ];

    // ----------------------------------------------------
    // CHARTS CONFIGURATION
    // ----------------------------------------------------

    // 1. Masters Overview (Column Chart)
    const mastersChartOptions = {
        chart: { type: "bar", toolbar: { show: false } },
        plotOptions: {
            bar: {
                horizontal: false,
                columnWidth: '40%',
                borderRadius: 4
            }
        },
        dataLabels: { enabled: false },
        colors: [primaryColor()],
        xaxis: {
            categories: ["Wings", "Floors", "Units"],
            labels: {
                style: {
                    colors: isDark ? "#FFFFFF" : "#64748B"
                }
            }
        },
        yaxis: {
            labels: {
                style: {
                    colors: isDark ? "#FFFFFF" : "#64748B"
                }
            }
        },
        fill: { opacity: 1 },
        tooltip: {
            y: { formatter: (val) => val }
        }
    };

    const mastersChartSeries = [
        { name: "Total Count", data: [masters?.totalWings || 0, masters?.totalFloors || 0, masters?.totalUnits || 0] }
    ];

    // 2. Unit Type Distribution (Donut Chart)
    const unitChartOptions = {
        chart: { type: "donut" },
        labels: ["Owned Units", "Rented Units"],
        colors: [primaryColor(), secondaryColor()],
        plotOptions: {
            pie: {
                donut: {
                    size: '78%',
                    labels: {
                        show: true,
                        name: {
                            show: true,
                            fontSize: '8px',
                            color: isDark ? '#94A3B8' : '#64748B',
                            fontWeight: 600,
                            offsetY: -6
                        },
                        value: {
                            show: true,
                            fontSize: '22px',
                            color: isDark ? '#FFFFFF' : '#0F172A',
                            fontWeight: 'bold',
                            offsetY: 4
                        },
                        total: {
                            show: true,
                            showAlways: true,
                            label: 'UNITS',
                            color: isDark ? '#94A3B8' : '#64748B',
                            formatter: function (w) {
                                return w.globals.seriesTotals.reduce((a, b) => a + b, 0)
                            }
                        }
                    }
                }
            }
        },
        dataLabels: { enabled: false },
        legend: {
            position: "bottom",
            labels: {
                colors: isDark ? "#FFFFFF" : "#64748B"
            }
        },
    };

    const unitChartSeries = [masters?.ownedUnits || 0, masters?.rentedUnits || 0];

    // 3. Complaints Breakdown (Donut Chart)
    const rolesChartOptions = {
        chart: { type: "donut" },
        labels: ["Open", "Processing", "Closed"],
        colors: ["#EF4444", "#F59E0B", "#10B981"],
        plotOptions: {
            pie: {
                donut: {
                    size: '78%',
                    labels: {
                        show: true,
                        name: {
                            show: true,
                            fontSize: '8px',
                            color: isDark ? '#94A3B8' : '#64748B',
                            fontWeight: 600,
                            offsetY: -6
                        },
                        value: {
                            show: true,
                            fontSize: '22px',
                            color: isDark ? '#FFFFFF' : '#0F172A',
                            fontWeight: 'bold',
                            offsetY: 4
                        },
                        total: {
                            show: true,
                            showAlways: true,
                            label: 'COMPLAINTS',
                            color: isDark ? '#94A3B8' : '#64748B',
                            formatter: function (w) {
                                return w.globals.seriesTotals.reduce((a, b) => a + b, 0)
                            }
                        }
                    }
                }
            }
        },
        dataLabels: { enabled: false },
        legend: {
            position: "bottom",
            labels: {
                colors: isDark ? "#FFFFFF" : "#64748B"
            }
        },
    };

    const rolesChartSeries = [complaints?.openComplaints || 0, complaints?.processingComplaints || 0, complaints?.closedComplaints || 0];

    // 4. Visitor Status Breakdown (Column Chart)
    const visitorChartOptions = {
        chart: { type: "bar", toolbar: { show: false } },
        plotOptions: {
            bar: {
                horizontal: false,
                columnWidth: '50%',
                borderRadius: 4
            }
        },
        dataLabels: { enabled: false },
        colors: [primaryColor(), "#3B82F6", "#10B981", "#F59E0B", "#EF4444", "#8B5CF6"],
        xaxis: {
            categories: [
                "Invited",
                "Non-Invited",
                "Approved",
                "Pending",
                "Checked In",
                "Checked Out"
            ],
            labels: {
                style: {
                    colors: isDark ? "#FFFFFF" : "#64748B"
                }
            }
        },
        yaxis: {
            labels: {
                style: {
                    colors: isDark ? "#FFFFFF" : "#64748B"
                }
            }
        },
        fill: { opacity: 1 },
        tooltip: {
            y: { formatter: (val) => val }
        }
    };

    const visitorChartSeries = [
        {
            name: "Visitors",
            data: [
                visitors?.invitedVisitors || 0,
                visitors?.nonInvitedVisitors || 0,
                visitors?.approvedVisitors || 0,
                visitors?.pendingVisitors || 0,
                visitors?.checkedInVisitors || 0,
                visitors?.checkedOutVisitors || 0
            ]
        }
    ];

    const dashboardActions = [
        {
            icon: faUserPlus,
            title: "Invite Visitor",
            description: "Pre-register a new visitor",
            url: "/zentcomm/visitor-managment/invited-visitor",
            iconStyles: { bg: "bg-indigo-100", color: "text-indigo-600" }
        },
        {
            icon: faClock,
            title: "Attendance Logs",
            description: "Check and monitor user attendance",
            url: "/zentcomm/attendance",
            iconStyles: { bg: "bg-blue-100", color: "text-blue-600" }
        },
        {
            icon: faWrench,
            title: "Complaints",
            description: "View and manage user complaints",
            url: "/zentcomm/complaint-management",
            iconStyles: { bg: "bg-red-100", color: "text-red-600" }
        },
        {
            icon: faBullhorn,
            title: "Announcements",
            description: "Post and view board announcements",
            url: "/zentcomm/announcement",
            iconStyles: { bg: "bg-purple-100", color: "text-purple-600" }
        }
    ];

    const currentDate = new Date();
    const currentHour = currentDate.getHours();
    const greeting = currentHour >= 5 && currentHour < 12 ? "Good Morning" : currentHour >= 12 && currentHour < 17 ? "Good Afternoon" : currentHour >= 17 && currentHour < 21 ? "Good Evening" : "Good Night";
    const formattedDate = currentDate.toLocaleString("en-US", { weekday: "long", day: "2-digit", month: "short", year: "numeric" });

    if (loading) {
        return (
            <div className="flex flex-col items-center justify-center h-full gap-3 text-textsecondary bg-dashboardbg">
                <svg className="w-10 h-10 animate-spin text-[#E31F26]" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                <span className="text-xs font-semibold">Loading Super Admin Dashboard...</span>
            </div>
        );
    }

    return (
        <div className="flex flex-col h-full overflow-hidden bg-dashboardbg">
            <div className="flex-1 overflow-y-auto p-4 lg:p-8 custom-scrollbar">
                <div className="max-w-[1600px] mx-auto w-full">

                    {/* Header */}
                    <div className="mb-6">
                        <h1 className="text-2xl font-bold text-textprimary mb-1">
                            {greeting}, {authUser?.name || "Super Admin"}! 👋
                        </h1>
                        <p className="text-sm text-textsecondary">
                            {formattedDate} • Here is your enterprise overview
                        </p>
                    </div>

                    {/* Top Metric Cards */}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
                        {topMetrics.map((metric, idx) => (
                            <MetricCard key={idx} {...metric} />
                        ))}
                    </div>

                    {/* Row 2: Master Summary & Unit Analytics */}
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
                        {/* Masters Overview Column Chart */}
                        <div className="bg-cardbg rounded-xl shadow-sm border border-bordercolor p-5">
                            <h3 className="text-sm font-bold text-textprimary mb-1">Master Records</h3>
                            <p className="text-xs text-textsecondary mb-4">Total count of setup properties</p>
                            <div className="h-[250px]">
                                <Chart options={mastersChartOptions} series={mastersChartSeries} type="bar" height="100%" />
                            </div>
                        </div>

                        {/* Unit Type Distribution Donut Chart */}
                        <div className="bg-cardbg rounded-xl shadow-sm border border-bordercolor p-5">
                            <h3 className="text-sm font-bold text-textprimary mb-1">Unit Occupancy</h3>
                            <p className="text-xs text-textsecondary mb-4">Distribution of owned vs rented units</p>
                            <div className="h-[250px] flex items-center justify-center">
                                <Chart options={unitChartOptions} series={unitChartSeries} type="donut" height="100%" />
                            </div>
                        </div>

                        {/* Quick Access */}
                        <ActionCenter
                            title="Quick Access"
                            description="Shortcuts to key management."
                            actions={dashboardActions}
                        />
                    </div>

                    {/* Row 3: Users Distribution & Visitor Summary */}
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
                        {/* Complaints Breakdown (Donut + Info List) */}
                        <div className="bg-cardbg rounded-xl shadow-sm border border-bordercolor p-5 flex flex-col justify-between">
                            <div>
                                <h3 className="text-sm font-bold text-textprimary mb-1">Complaints Breakdown</h3>
                                <p className="text-xs text-textsecondary mb-4">Enterprise complaints status</p>
                                <div className="h-[200px] flex items-center justify-center mb-4">
                                    <Chart options={rolesChartOptions} series={rolesChartSeries} type="donut" height="100%" />
                                </div>
                            </div>
                            <div className="space-y-2 border-t border-bordercolor pt-3">
                                <div className="flex justify-between items-center text-xs">
                                    <span className="font-semibold text-textsecondary">Open</span>
                                    <span className="font-bold text-textprimary">{complaints?.openComplaints || 0}</span>
                                </div>
                                <div className="flex justify-between items-center text-xs">
                                    <span className="font-semibold text-textsecondary">Processing</span>
                                    <span className="font-bold text-textprimary">{complaints?.processingComplaints || 0}</span>
                                </div>
                                <div className="flex justify-between items-center text-xs">
                                    <span className="font-semibold text-textsecondary">Closed</span>
                                    <span className="font-bold text-textprimary">{complaints?.closedComplaints || 0}</span>
                                </div>
                            </div>
                        </div>

                        {/* Visitor Status Summary */}
                        <div className="lg:col-span-2 bg-cardbg rounded-xl shadow-sm border border-bordercolor p-5">
                            <div className="flex justify-between items-center mb-1">
                                <h3 className="text-sm font-bold text-textprimary">Visitor Overview</h3>
                                <span className="bg-red-50 text-red-500 text-[10px] px-2 py-1 rounded font-semibold flex items-center gap-1">
                                    <div className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse"></div> Live
                                </span>
                            </div>
                            <p className="text-xs text-textsecondary mb-4">Enterprise visitor status summaries</p>
                            <div className="h-[280px]">
                                <Chart options={visitorChartOptions} series={visitorChartSeries} type="bar" height="100%" />
                            </div>
                        </div>
                    </div>

                    {/* Row 4: Enterprise Notices & Complaints Summary */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {/* Notices Card */}
                        <div className="bg-cardbg rounded-xl shadow-sm border border-bordercolor p-5 flex items-center justify-between">
                            <div>
                                <h4 className="text-xs font-bold text-textsecondary uppercase tracking-wider mb-1">Total Announcements / Notices</h4>
                                <h3 className="text-2xl font-bold text-textprimary">{notices?.totalNotices || 0}</h3>
                                <div className="flex gap-4 mt-3 text-xs">
                                    <span className="text-green-600 font-semibold">{notices?.publishedNotices || 0} Published</span>
                                    <span className="text-textsecondary font-semibold">{notices?.draftNotices || 0} Drafts</span>
                                    <span className="text-red-500 font-semibold">{notices?.expiredNotices || 0} Expired</span>
                                </div>
                            </div>
                            <div className="w-12 h-12 bg-purple-50 text-purple-600 rounded-xl flex items-center justify-center text-lg">
                                <FontAwesomeIcon icon={faBullhorn} />
                            </div>
                        </div>

                        {/* Complaints Card */}
                        <div
                            onClick={() => navigate("/zentcomm/complaint-management")}
                            className="bg-cardbg rounded-xl shadow-sm border border-bordercolor p-5 flex items-center justify-between cursor-pointer hover:border-red-300 transition-colors"
                        >
                            <div>
                                <h4 className="text-xs font-bold text-textsecondary uppercase tracking-wider mb-1">Total Maintenance / Complaints</h4>
                                <h3 className="text-2xl font-bold text-textprimary">{complaints?.totalComplaints || 0}</h3>
                                <div className="flex gap-4 mt-3 text-xs">
                                    <span className="text-red-500 font-semibold">Active Support Needed</span>
                                </div>
                            </div>
                            <div className="w-12 h-12 bg-secondaryaccent text-primaryaccent border border-accentborder rounded-xl flex items-center justify-center text-lg">
                                <FontAwesomeIcon icon={faWrench} />
                            </div>
                        </div>
                    </div>

                </div>
            </div>
        </div>
    );
};

export default SuperAdminDashboard;
